import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import SoulOrb from "@/components/SoulOrb";

/**
 * SoulRitualToast — the brief, sensory overlay that appears when a
 * Mind or Body ritual is completed. Renders a warm confident orb
 * with a soft expansion plus a short micro-message. Purely visual —
 * the 432Hz tone that used to accompany this celebration was removed
 * as part of the app-wide UI-sound cleanup.
 *
 * Two variants:
 *   • Ritual completion (default) — "Your soul is fulfilled." — ~2.4s
 *   • Streak milestone           — "{label}. Your soul is expanding."
 *                                  Larger, longer (~4.0s)
 *
 * Fire from anywhere:
 *   window.dispatchEvent(new CustomEvent("soul-ritual-completed"));
 *   window.dispatchEvent(new CustomEvent("soul-ritual-completed", {
 *     detail: { milestone: 7, label: "A week unbroken" }
 *   }));
 */

const RITUAL_MS    = 2400;
const MILESTONE_MS = 4200;

export default function SoulRitualToast() {
  const [state, setState] = useState(null);  // { seed, milestone, label }

  useEffect(() => {
    const onFire = (ev) => {
      const detail = ev?.detail || {};
      setState({
        seed: Date.now(),
        milestone: detail.milestone || 0,
        label: detail.label || "",
      });
    };
    window.addEventListener("soul-ritual-completed", onFire);
    return () => window.removeEventListener("soul-ritual-completed", onFire);
  }, []);

  useEffect(() => {
    if (!state) return;
    const dur = state.milestone ? MILESTONE_MS : RITUAL_MS;
    const t = setTimeout(() => setState(null), dur);
    return () => clearTimeout(t);
  }, [state]);

  if (!state) return null;

  const isMilestone = !!state.milestone;
  const message = isMilestone
    ? `${state.label || `${state.milestone} days`} — your soul is expanding.`
    : "Your soul is fulfilled.";

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      data-testid={isMilestone ? "soul-milestone-toast" : "soul-ritual-toast"}
      data-milestone={state.milestone || undefined}
      className="fixed inset-0 z-[70] pointer-events-none flex items-center justify-center"
      style={{
        animation: `${isMilestone ? "soul-milestone-fade" : "soul-ritual-fade"} ${
          isMilestone ? MILESTONE_MS : RITUAL_MS
        }ms ease-out forwards`,
      }}
    >
      <div className="flex flex-col items-center gap-6">
        <SoulOrb
          size={isMilestone ? "lg" : "lg"}
          mode="confident"
          expandSeed={state.seed}
          pulseSeed={state.seed}
          ariaLabel={isMilestone ? "Soul orb — a streak milestone was reached" : "Soul orb — a ritual was completed"}
          testid={isMilestone ? "soul-milestone-toast-orb" : "soul-ritual-toast-orb"}
          className={isMilestone ? "scale-125" : ""}
        />
        {isMilestone && (
          <div
            className="text-[11px] uppercase tracking-[0.32em] text-[hsl(var(--terracotta))]"
            data-testid="soul-milestone-toast-eyebrow"
          >
            Streak milestone
          </div>
        )}
        <p
          className="font-display italic text-2xl sm:text-3xl md:text-4xl text-[hsl(var(--fg))] text-center px-6"
          data-testid={isMilestone ? "soul-milestone-toast-message" : "soul-ritual-toast-message"}
        >
          {message}
        </p>
      </div>
    </div>,
    document.body,
  );
}

/**
 * celebrateRitualCompleted — the every-ritual variant. Fires the base event.
 */
export function celebrateRitualCompleted() {
  try { window.dispatchEvent(new CustomEvent("soul-ritual-completed")); } catch { /* noop */ }
}

/**
 * celebrateStreakMilestone — larger celebration for 7 / 14 / 30 day streaks.
 */
export function celebrateStreakMilestone(days, label) {
  try {
    window.dispatchEvent(new CustomEvent("soul-ritual-completed", {
      detail: { milestone: days, label: label || `${days} days` },
    }));
  } catch { /* noop */ }
}
